import axios from "axios";
import productsData from "@/data/products.json";
import faqsData from "@/data/faqs.json";
import collectionsData from "@/data/collections.json";
import articlesData from "@/data/articles.json";
import coasData from "@/data/coas.json";
import storeConfigData from "@/data/store-config.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";
const HAS_BACKEND = Boolean(BACKEND_URL);

const client = axios.create({ baseURL: API, timeout: 8000 });

/**
 * The site ships a static JSON snapshot of the catalog + editorial content
 * so it renders end-to-end even when the backend is not reachable (e.g. the
 * Netlify build has no REACT_APP_BACKEND_URL). Whenever the backend IS
 * available we prefer live responses so real-time Shift4Shop overrides win.
 */
const tryBackend = async (fn, fallback) => {
  if (!HAS_BACKEND) return fallback;
  try {
    return await fn();
  } catch (_) {
    return fallback;
  }
};

const filterProducts = ({ collection, availability, q, sort } = {}) => {
  let items = [...productsData.products];
  if (collection && collection !== "all") {
    items = items.filter((p) => p.collection === collection);
  }
  if (availability && availability !== "all") {
    items = items.filter((p) => p.availability === availability);
  }
  if (q) {
    const ql = q.trim().toLowerCase();
    items = items.filter(
      (p) =>
        p.name.toLowerCase().includes(ql) ||
        (p.short || "").toLowerCase().includes(ql) ||
        (p.collection_name || "").toLowerCase().includes(ql)
    );
  }
  if (sort === "name-asc") items.sort((a, b) => a.name.localeCompare(b.name));
  else if (sort === "name-desc") items.sort((a, b) => b.name.localeCompare(a.name));
  else items.sort((a, b) => Number(!a.featured) - Number(!b.featured) || a.name.localeCompare(b.name));
  return { count: items.length, products: items };
};

export const getStoreConfig = () =>
  tryBackend(() => client.get("/store-config").then((r) => r.data), storeConfigData);

export const getCollections = () =>
  tryBackend(() => client.get("/collections").then((r) => r.data), collectionsData);

export const getCollection = (slug) =>
  tryBackend(
    () => client.get(`/collections/${slug}`).then((r) => r.data),
    (() => {
      const col = collectionsData.find((c) => c.slug === slug) || null;
      const products = productsData.products.filter((p) => p.collection === slug);
      return col ? { collection: col, products } : null;
    })()
  );

export const getProducts = (params = {}) =>
  tryBackend(() => client.get("/products", { params }).then((r) => r.data), filterProducts(params));

export const getProduct = (slug) =>
  tryBackend(
    () => client.get(`/products/${slug}`).then((r) => r.data),
    (() => {
      const product = productsData.products.find((p) => p.slug === slug) || null;
      const related = product
        ? productsData.products.filter((p) => p.collection === product.collection && p.slug !== slug).slice(0, 3)
        : [];
      return product ? { product, related } : null;
    })()
  );

export const getArticles = () =>
  tryBackend(() => client.get("/articles").then((r) => r.data), articlesData);

export const getArticle = (slug) =>
  tryBackend(
    () => client.get(`/articles/${slug}`).then((r) => r.data),
    articlesData.find((a) => a.slug === slug) || null
  );

export const getFaqs = () =>
  tryBackend(() => client.get("/faqs").then((r) => r.data), faqsData);

export const getCoas = (q) =>
  tryBackend(
    () => client.get("/coas", { params: q ? { q } : {} }).then((r) => r.data),
    coasData
  );

/**
 * Contact submissions: prefer the backend (which mirrors to Resend + Mongo),
 * fall back to a resolved promise so the UI can also rely on Netlify Forms.
 */
export const postContact = (data) =>
  tryBackend(
    () => client.post("/contact", data).then((r) => r.data),
    { success: true, email_delivered: false, message: "Thank you. Your message has been received." }
  );

export default client;
